import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export interface SortControls extends SortState {
  setSort: (column: string, direction?: SortDirection) => void;
  toggleSort: (column: string) => void;
  clearSort: () => void;
}

export function useSortState(
  defaultColumn: string | null = null,
  defaultDirection: SortDirection = 'desc',
): SortControls {
  const [searchParams, setSearchParams] = useSearchParams();

  const column = searchParams.get('sort') ?? defaultColumn;
  const direction = (searchParams.get('dir') as SortDirection | null) ?? defaultDirection;

  const setSort = useCallback((newColumn: string, newDirection: SortDirection = 'asc') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('sort', newColumn);
      next.set('dir', newDirection);
      next.delete('page');
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  const toggleSort = useCallback((targetColumn: string) => {
    if (column !== targetColumn) {
      setSort(targetColumn, 'desc');
    } else if (direction === 'desc') {
      setSort(targetColumn, 'asc');
    } else {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('sort');
        next.delete('dir');
        next.delete('page');
        return next;
      });
    }
  }, [column, direction, setSort, setSearchParams]);

  const clearSort = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('sort');
      next.delete('dir');
      next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  return { column, direction, setSort, toggleSort, clearSort };
}
